import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight, ArrowUpRight, Loader2 } from 'lucide-react';

import WhatsAppIcon from '@/components/icons/WhatsAppIcon';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import whatsappService from '@/services/whatsapp.service';

interface WhatsAppCardProps {
  /** Schmale Seitenleiste — dann nur das Symbol. */
  collapsed?: boolean;
}

/**
 * WhatsApp-Karte in der Seitenleiste — Schnellzugriff auf die letzten
 * Unterhaltungen (ein-/ausgehend) mit Direktantwort im 24-h-Fenster.
 *
 * Erscheint NUR, wenn mindestens eine Mitglieds-Organisation WhatsApp
 * aktiv verbunden hat — sonst wäre die Karte totes UI.
 */
export default function WhatsAppCard({ collapsed = false }: WhatsAppCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const { data } = useQuery({
    queryKey: ['wa-conversations'],
    queryFn: () => whatsappService.conversations(),
    // Popover offen: fast live; zu: entspanntes Polling für den Badge.
    refetchInterval: open ? 10_000 : 60_000,
    staleTime: 8_000,
    retry: false,
  });

  if (!data?.configured) return null;

  const items = data.items;
  // „Wartet auf Antwort": jüngste Nachricht kam vom Kunden.
  const awaiting = items.filter((c) => c.direction === 'in').length;
  const current = items.find((c) => c.ticket_id === selected) || null;

  const timeAgo = (iso: string) => {
    const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (min < 1) return t('shell:wa.justNow', 'jetzt');
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} h`;
    return new Date(iso).toLocaleDateString(i18n.language);
  };

  const send = async () => {
    const trimmed = text.trim();
    if (!current || !trimmed || sending) return;
    setSending(true);
    try {
      await whatsappService.quickReply(current.ticket_id, trimmed);
      setText('');
      toast.success(t('shell:wa.sent', 'Über WhatsApp gesendet'));
      void queryClient.invalidateQueries({ queryKey: ['wa-conversations'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('shell:wa.sendFailed', 'Senden fehlgeschlagen'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSelected(null); setText(''); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'vyndesk-call-status flex items-center gap-2 rounded-md border text-xs font-medium transition',
            collapsed ? 'h-8 w-8 justify-center px-0' : 'h-9 w-full px-2.5',
          )}
          title="WhatsApp"
        >
          <WhatsAppIcon className="h-3.5 w-3.5 shrink-0" />
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate text-left">WhatsApp</span>
              {awaiting > 0 && (
                <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-emerald-700">
                  {awaiting}
                </span>
              )}
            </>
          )}
          {collapsed && awaiting > 0 && (
            <span className="absolute -mt-4 ml-4 rounded bg-emerald-500 px-1 text-[9px] font-bold text-white">
              {awaiting}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent side="right" align="end" className="w-80 rounded-md p-0">
        <div className="border-b border-border px-3 py-2 text-xs font-semibold text-foreground">
          {t('shell:wa.recent', 'Letzte WhatsApp-Unterhaltungen')}
        </div>

        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            {t('shell:wa.empty', 'Noch keine Unterhaltungen')}
          </p>
        ) : (
          <ul className="max-h-72 overflow-y-auto py-1">
            {items.map((c) => (
              <li key={c.ticket_id}>
                <button
                  type="button"
                  onClick={() => { setSelected(c.ticket_id === selected ? null : c.ticket_id); setText(''); }}
                  className={cn(
                    'flex w-full flex-col gap-0.5 px-3 py-2 text-left transition hover:bg-accent',
                    selected === c.ticket_id && 'bg-accent',
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-xs font-semibold text-foreground">
                      {c.subject || c.wa_number || 'WhatsApp'}
                    </span>
                    <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">{timeAgo(c.created_at)}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    {/* Richtungspfeil: ← Kunde schrieb, → wir schrieben */}
                    <span className={cn('shrink-0 font-bold', c.direction === 'in' ? 'text-emerald-500' : 'text-muted-foreground/70')}>
                      {c.direction === 'in' ? '←' : '→'}
                    </span>
                    <span className="min-w-0 truncate">{c.body || '…'}</span>
                  </span>
                </button>

                {/* Ausgeklappt: Direktantwort + Ticket-Link */}
                {selected === c.ticket_id && (
                  <div className="space-y-1.5 border-b border-border/60 px-3 pb-2.5 pt-1">
                    {c.window_open ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          autoFocus
                          value={text}
                          onChange={(e) => setText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') void send(); }}
                          placeholder={t('shell:wa.replyPlaceholder', 'Antwort schreiben …')}
                          className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                        {/* Minimaler Pfeil statt Papierflieger (ausdrücklicher Wunsch) */}
                        <button
                          type="button"
                          onClick={() => void send()}
                          disabled={sending || !text.trim()}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:opacity-40"
                          title={t('shell:wa.send', 'Senden')}
                        >
                          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                        </button>
                      </div>
                    ) : (
                      <p className="text-[11px] leading-4 text-amber-600">
                        {t('shell:wa.windowClosed', '24-h-Fenster geschlossen — Antwort nur im Ticket (Vorlage nötig).')}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => { setOpen(false); navigate(`/tickets/${c.ticket_id}`); }}
                      className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                    >
                      <ArrowUpRight className="h-3 w-3" />
                      {t('shell:wa.openTicket', 'Ticket öffnen')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
